/**
 * Newsletter / Marketing Email domain.
 *
 * Architecture:
 *
 *   Subscribers ─┐
 *                ├─→  Audience (segment/list)
 *   Audience ────┤
 *                └─→  Campaign (uses a Template + targets an Audience)
 *   Template ────┘              │
 *                                ▼
 *                             Sends (one per recipient, tracks delivery + opens + clicks)
 *                                │
 *                                ▼
 *                      Suppressions (bounces, complaints, unsubscribes)
 *
 * CAN-SPAM compliance: every campaign includes a visible unsubscribe link
 * and physical mailing address. Unsubscribes are honored immediately and
 * respected on all future sends.
 */

// ─────────── Subscriber ───────────

export type SubscriberStatus =
  | 'active'            // confirmed, sendable
  | 'unsubscribed'      // opted out via footer link
  | 'bounced'           // hard bounced — permanently undeliverable
  | 'complained'        // marked as spam
  | 'pending';          // imported but not yet confirmed (for double opt-in flows)

export const SUBSCRIBER_STATUS_LABELS: Record<SubscriberStatus, string> = {
  active:       'Active',
  unsubscribed: 'Unsubscribed',
  bounced:      'Bounced',
  complained:   'Complained',
  pending:      'Pending',
};

export type Subscriber = {
  id: string;
  email: string;                // lowercase, canonical
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  phone?: string;

  status: SubscriberStatus;

  source?: string;               // 'csv_import', 'manual', 'website_form', 'clients_sync'
  sourceRef?: string;            // id of the import batch or client record
  clientId?: string;             // if derived from a CRM client
  tags?: string[];
  audienceIds?: string[];        // which audiences this sub belongs to
  locale?: string;               // 'en', 'es' — matches Talbert's bilingual needs

  customFields?: Record<string, string>;

  // Engagement scores (computed from sends)
  totalSends?: number;
  totalOpens?: number;
  totalClicks?: number;
  lastSentAt?: string;
  lastOpenedAt?: string;
  lastClickedAt?: string;

  subscribedAt?: string;
  unsubscribedAt?: string;
  unsubscribeReason?: string;

  createdAt?: string;
  updatedAt?: string;
};

// ─────────── Audience ───────────

export type AudienceKind = 'static' | 'dynamic';

export type AudienceRule = {
  field: 'status' | 'tag' | 'locale' | 'clientType' | 'lastOpened' | 'lastClicked' | 'engagement';
  op: 'equals' | 'not_equals' | 'contains' | 'in' | 'before' | 'after' | 'at_least' | 'at_most';
  value: string | number | string[];
};

export type Audience = {
  id: string;
  name: string;
  description?: string;
  kind: AudienceKind;
  /** Dynamic segment rules (evaluated at send time) */
  rules?: AudienceRule[];
  /** For static audiences — count is manually maintained */
  subscriberCount?: number;
  color?: string;                // UI accent
  archived?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

// ─────────── Template ───────────

export type BlockType =
  | 'heading'
  | 'text'
  | 'button'
  | 'image'
  | 'divider'
  | 'spacer'
  | 'columns'
  | 'social'
  | 'footer';

export type Block = {
  id: string;
  type: BlockType;
  /** Content is block-type specific */
  content: Record<string, unknown>;
  /** Styling overrides */
  styles?: Record<string, string>;
};

export type TemplateTheme = {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  headingFont: string;
  bodyFont: string;
  containerWidth: number;
};

export const DEFAULT_THEME: TemplateTheme = {
  primaryColor:    '#85572e',   // Talbert brass
  backgroundColor: '#faf6f1',
  textColor:       '#2c2720',
  headingFont:     'Georgia, "Cormorant Garamond", serif',
  bodyFont:        '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  containerWidth:  640,
};

export type Template = {
  id: string;
  name: string;
  description?: string;
  category?: 'announcement' | 'newsletter' | 'greeting' | 'promotion' | 'transactional';
  locale?: 'en' | 'es';
  subject?: string;
  previewText?: string;
  fromName?: string;

  blocks: Block[];
  theme: TemplateTheme;

  /** Compiled HTML cache — regenerated on save */
  compiledHtml?: string;
  compiledText?: string;

  archived?: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
};

// ─────────── Campaign ───────────

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'paused'
  | 'cancelled'
  | 'failed';

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft:     'Draft',
  scheduled: 'Scheduled',
  sending:   'Sending',
  sent:      'Sent',
  paused:    'Paused',
  cancelled: 'Cancelled',
  failed:    'Failed',
};

export type Campaign = {
  id: string;
  name: string;                       // internal label
  subject: string;                    // email subject line
  previewText?: string;                // preheader text

  fromName: string;
  fromEmail: string;
  replyTo?: string;

  templateId?: string;                 // reference to source template
  /** The blocks + theme snapshot at send time — so edits to the source
   *  template don't affect already-sent campaigns. */
  blocks: Block[];
  theme: TemplateTheme;

  /** Whom to send to */
  audienceIds: string[];
  /** Exclude subscribers in these audiences */
  excludeAudienceIds?: string[];

  status: CampaignStatus;

  // Scheduling
  scheduledFor?: string;               // ISO datetime
  timezone?: string;                   // e.g. 'America/Los_Angeles'

  // Send timing
  sendStartedAt?: string;
  sendCompletedAt?: string;

  // Recipient targeting
  estimatedRecipientCount?: number;
  actualRecipientCount?: number;

  // Aggregate metrics (updated as sends stream in)
  metrics?: {
    queued: number;
    sent: number;
    delivered: number;
    opens: number;
    uniqueOpens: number;
    clicks: number;
    uniqueClicks: number;
    bounces: number;
    complaints: number;
    unsubscribes: number;
  };

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
};

// ─────────── Send record ───────────

export type SendStatus =
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'complained'
  | 'unsubscribed'
  | 'failed';

export type SendRecord = {
  id: string;
  campaignId: string;
  subscriberId: string;
  email: string;                   // denormalized for auditability

  status: SendStatus;
  messageId?: string;              // SES Message-ID
  sentAt?: string;
  deliveredAt?: string;

  opens: number;
  firstOpenAt?: string;
  lastOpenAt?: string;

  clicks: number;
  firstClickAt?: string;
  lastClickAt?: string;
  clickedUrls?: Record<string, number>;

  bounceType?: 'Permanent' | 'Transient' | 'Undetermined';
  bounceSubType?: string;
  complaintAt?: string;
  unsubscribedAt?: string;
  failureReason?: string;
};

// ─────────── Suppressions ───────────

export type SuppressionReason =
  | 'unsubscribe'
  | 'hard_bounce'
  | 'complaint'
  | 'manual'
  | 'list_cleaning';

export type Suppression = {
  id: string;
  email: string;
  reason: SuppressionReason;
  campaignId?: string;
  createdAt?: string;
  notes?: string;
};

// ─────────── Helpers ───────────

export function subscriberName(s: Subscriber): string {
  const parts = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
  return parts || s.email;
}

export function mergeTagsInString(str: string, s: Pick<Subscriber, 'firstName' | 'lastName' | 'email' | 'company'>) {
  return str
    .replace(/\{\{\s*firstName\s*\}\}/g, s.firstName ?? '')
    .replace(/\{\{\s*lastName\s*\}\}/g, s.lastName ?? '')
    .replace(/\{\{\s*email\s*\}\}/g, s.email)
    .replace(/\{\{\s*company\s*\}\}/g, s.company ?? '');
}

/** Canonicalize an email for dedup comparison */
export function canonicalEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Basic email format check (defensive — server does authoritative validation) */
export function isLikelyEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
